import { ValueOf } from 'lib/core/types';

import taskBuilder from 'lib/common/contexts/ContactContext/utils/taskBuilder/index';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import CONNECTION_TYPE_MAP from 'lib/common/constants/connectionType';
import {
  OUTBOUND_CAMPAIGN as OUTBOUND_CAMPAIGN_ATTRIBUTE,
  OUTBOUND_CAMPAIGN_DESCRIPTION as OUTBOUND_CAMPAIGN_DESCRIPTION_ATTRIBUTE,
} from 'lib/common/constants/contactAttributes';
import CONTACT_STATES from 'lib/common/constants/contactStates';

import PreviewDialingTask from 'lib/common/types/PreviewDialingTask';
import connectGetter from 'lib/common/utils/connectGetter';

import { PREVIEW_DIALING_CONNECT_CONTACT_STATE_MAP } from './constants';

export default async function newPreviewDialingTask({
  contact,
  status,
}: {
  contact: connect.Contact;
  status?: ValueOf<typeof CONTACT_STATES>;
}): Promise<PreviewDialingTask | null> {
  const newTask = taskBuilder.newBaseTask(contact, status);

  if (!newTask) {
    return null;
  }

  const contactAttributes = connectGetter(contact, 'getAttributes');

  // Connect can sometimes throw an error when calling isPreviewMode or getPreviewConfiguration, so we wrap them in try/catch to prevent the entire task creation from failing

  let isPreviewMode = false;
  try {
    // @ts-expect-error - This method is missing from the connect typings, but it does exist on the contact object
    isPreviewMode = await contact.isPreviewMode(contact.contactId);
  } catch (error) {
    logger.error(LogEvents.PREVIEW_DIALING_TASK.IS_PREVIEW_MODE_ERROR, { error });
  }

  let previewConfiguration = undefined;
  try {
    // @ts-expect-error - This method is missing from the connect typings, but it does exist on the contact object
    previewConfiguration = await contact.getPreviewConfiguration(contact.contactId);
  } catch (error) {
    logger.error(LogEvents.PREVIEW_DIALING_TASK.GET_PREVIEW_CONFIGURATION_ERROR, { error });
  }

  const previewTaskContent = {
    isPreviewMode,
    previewConfiguration,
    campaignName: contactAttributes?.[OUTBOUND_CAMPAIGN_ATTRIBUTE]?.value,
    description: contactAttributes?.[OUTBOUND_CAMPAIGN_DESCRIPTION_ATTRIBUTE]?.value || '',
    outboundPhoneNumber: connectGetter(contact, 'getCustomerEndpoint')?.phoneNumber || '',
  };

  const contactStateType = connectGetter(contact, 'getState')?.type;

  // newBaseTask converts Pending into Accepted and Error into Missed
  // for other task types
  // but for preview dialing tasks, we want to keep it as is
  const updatedStatus = (contactStateType && PREVIEW_DIALING_CONNECT_CONTACT_STATE_MAP[contactStateType]) || status;

  // Preview Dialing task connections are always of type INBOUND,
  // but we need to differentiate to update UI
  // when task has been accepted(in pending state)
  const connectionType =
    contactStateType === connect.ContactStateType.PENDING ? CONNECTION_TYPE_MAP.AGENT : CONNECTION_TYPE_MAP.INBOUND;

  return {
    ...newTask,
    ...previewTaskContent,
    connectionType,
    connectionValue: previewTaskContent.campaignName || previewTaskContent.outboundPhoneNumber,
    ...(updatedStatus ? { status: updatedStatus } : {}),
  };
}
